import { useEffect, useState } from "react";
import { C, F } from "../theme/tokens";
import { Avviso, Bottone, Scheda } from "./ui";

/**
 * Parole salvate: la lista personale di parole e frasi da oscurare (piani
 * Silver e superiori). E' di chi la scrive, il team non la vede, e non si
 * applica da sola: all'invio serve spuntare la casella.
 */

/** Una per riga o separate da virgola; spazi compattati, doppioni via. */
export function dividiParole(testo) {
  const visti = new Set();
  const out = [];
  for (const grezza of String(testo || "").split(/[\n,]/)) {
    const voce = grezza.trim().replace(/\s+/g, " ");
    const chiave = voce.toLocaleLowerCase("it");
    if (voce && !visti.has(chiave)) {
      visti.add(chiave);
      out.push(voce);
    }
  }
  return out;
}

const chip = {
  display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, fontFamily: F.b,
  color: C.smartBlue, background: C.smartBlue20, border: `1px solid ${C.border}`, borderRadius: 999, padding: "4px 10px",
};
const linkTesto = {
  background: "none", border: "none", padding: 0, color: C.smartBlue, fontWeight: 600, fontSize: 12,
  fontFamily: F.b, cursor: "pointer", textDecoration: "underline",
};

/** Area clienti: aggiungere e togliere voci dalla propria lista. */
export function GestioneParole({ api }) {
  const [voci, setVoci] = useState(null); // null: in caricamento
  const [massimo, setMassimo] = useState(500);
  const [testo, setTesto] = useState("");
  const [errore, setErrore] = useState(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.parole()
      .then((r) => { if (vivo) { setVoci(r.parole || []); setMassimo(r.massimo || 500); } })
      .catch((e) => { if (vivo) { setVoci([]); setErrore(e.message); } });
    return () => { vivo = false; };
  }, [api]);

  const nuove = dividiParole(testo);
  const aggiungi = async (ev) => {
    ev.preventDefault();
    if (!nuove.length) return;
    setInCorso(true);
    setErrore(null);
    try {
      const r = await api.aggiungiParole(nuove);
      setVoci(r.parole || []);
      setTesto("");
    } catch (e) {
      setErrore(e.message);
    } finally {
      setInCorso(false);
    }
  };
  const togli = async (voce) => {
    setErrore(null);
    try {
      const r = await api.rimuoviParola(voce.id);
      setVoci(r.parole || []);
    } catch (e) {
      setErrore(e.message);
    }
  };

  return (
    <Scheda
      titolo="Le tue parole salvate"
      descrizione='Nomi, luoghi o codici che oscuri spesso. La lista è solo tua: i colleghi non la vedono. Quando anonimizzi la usi spuntando "Usa anche le mie parole salvate".'
    >
      <form onSubmit={aggiungi}>
        <label htmlFor="parole-nuove" style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.textTitle, marginBottom: 6 }}>
          Aggiungi parole o frasi
        </label>
        <textarea
          id="parole-nuove"
          value={testo}
          onChange={(e) => setTesto(e.target.value)}
          placeholder={"Una per riga o separate da virgola\nMario Rossi\nStudio Bianchi"}
          rows={3}
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical", borderRadius: 10, border: `1px solid ${C.border}`, padding: "10px 12px", fontSize: 13, fontFamily: F.b, color: C.textBody, background: C.smartWhite, outline: "none", marginBottom: 10 }}
        />
        <Bottone tipo="submit" variante="secondario" disabilitato={inCorso || !nuove.length}>
          {inCorso ? "Salvataggio..." : "Aggiungi alla lista"}
        </Bottone>
      </form>
      {errore && <Avviso tipo="errore" style={{ marginTop: 10 }}>{errore}</Avviso>}
      <div style={{ marginTop: 16 }}>
        {voci === null ? (
          <p style={{ margin: 0, fontSize: 13, color: C.textNum }}>Caricamento...</p>
        ) : voci.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: C.textNum }}>La lista è vuota.</p>
        ) : (
          <>
            <p style={{ margin: "0 0 8px", fontSize: 12, color: C.textNum }}>{voci.length} di {massimo}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {voci.map((v) => (
                <span key={v.id} style={chip}>
                  {v.testo}
                  <button
                    type="button"
                    onClick={() => togli(v)}
                    aria-label={`Togli "${v.testo}" dalla lista`}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: C.smartBlue, fontSize: 14, lineHeight: 1 }}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </Scheda>
  );
}

/**
 * Pagina di anonimizzazione: la casella per usare la lista in questo invio,
 * spenta all'inizio, e l'elenco che si apre a richiesta.
 */
export function UsaParoleSalvate({ voci, usa, onUsa, onGestisci }) {
  const [aperto, setAperto] = useState(false);
  const n = voci.length;
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontFamily: F.b, color: n ? C.textBody : C.textNum, cursor: n ? "pointer" : "default" }}>
          <input
            type="checkbox"
            checked={usa && n > 0}
            disabled={!n}
            onChange={() => onUsa(!usa)}
            style={{ width: 16, height: 16, accentColor: C.smartBlue, flexShrink: 0 }}
          />
          Usa anche le mie parole salvate ({n})
        </label>
        {n > 0 && (
          <button type="button" onClick={() => setAperto((a) => !a)} aria-expanded={aperto} style={linkTesto}>
            {aperto ? "Nascondi" : "Mostra"}
          </button>
        )}
      </div>
      {!n && (
        <p style={{ margin: "6px 0 0", fontSize: 12, color: C.textNum }}>
          La tua lista è vuota: <button type="button" onClick={onGestisci} style={linkTesto}>aggiungi parole nell&apos;area clienti</button>.
        </p>
      )}
      {aperto && n > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {voci.map((v) => <span key={v.id} style={chip}>{v.testo}</span>)}
          </div>
          <button type="button" onClick={onGestisci} style={{ ...linkTesto, marginTop: 8 }}>Modifica la lista nell&apos;area clienti</button>
        </div>
      )}
    </div>
  );
}
